import axios from 'axios';

import {
    ExerciseItemResponse,
    ExerciseMutationResponse,
    ExerciseRecord,
    ExerciseSearchFilters,
    ExerciseSearchItem,
    ExerciseVideoUploadItem,
    ExerciseVideoUploadResponse,
    SearchExercisesResponse,
} from './exercises.interface';
import { ApiError } from '../common/errors';

const EXERCISES_ENDPOINT = '/api/exercises';
const EXERCISE_SEARCH_ENDPOINT = '/api/exercises/search';
const EXERCISE_VIDEO_UPLOAD_ENDPOINT = '/api/exercises/video-upload';

const createApiError = (error: any, fallbackMessage: string): Error => {
    if (error.response?.status === 404) {
        throw new ApiError(error.response?.data?.message || fallbackMessage);
    }

    if (error.response?.status === 400) {
        throw new ApiError(error.response?.data?.message || fallbackMessage);
    }

    throw new Error(error.response?.data?.message || error.message || fallbackMessage);
};

const createSearchPayload = (filters: ExerciseSearchFilters): ExerciseSearchFilters => {
    const payload: ExerciseSearchFilters = {};

    if (filters.level) {
        payload.level = filters.level;
    }

    if (filters.name) {
        payload.name = filters.name;
    }

    if (filters.tag !== undefined) {
        payload.tag = filters.tag;
    }

    if (filters.tags?.length) {
        payload.tags = filters.tags;
    }

    return payload;
};

export const searchExercises = async (filters: ExerciseSearchFilters): Promise<ExerciseSearchItem[]> => {
    try {
        const payload = createSearchPayload(filters);
        const { data } = await axios.post(EXERCISE_SEARCH_ENDPOINT, payload);

        return (data as SearchExercisesResponse).items;
    } catch (error: any) {
        return createApiError(error, 'Unable to search exercises.');
    }
};

export const loadExercisesByTags = async (tags: number[]): Promise<ExerciseSearchItem[]> => {
    const uniqueTags = Array.from(new Set(tags.filter((tag) => Number.isInteger(tag) && tag >= 0)));

    if (!uniqueTags.length) {
        return [];
    }

    return searchExercises({ tags: uniqueTags });
};

export const getExerciseByIndex = async (index: number): Promise<ExerciseRecord> => {
    try {
        const { data } = await axios.get(`${EXERCISES_ENDPOINT}/${index}`);

        return (data as ExerciseItemResponse).item;
    } catch (error: any) {
        return createApiError(error, 'Unable to load the exercise.');
    }
};

export const createExercise = async (exercise: ExerciseRecord): Promise<ExerciseMutationResponse> => {
    try {
        const { data } = await axios.post(EXERCISES_ENDPOINT, exercise);

        return data as ExerciseMutationResponse;
    } catch (error: any) {
        return createApiError(error, 'Unable to create the exercise.');
    }
};

export const updateExercise = async (index: number, exercise: ExerciseRecord): Promise<ExerciseMutationResponse> => {
    try {
        const { data } = await axios.put(`${EXERCISES_ENDPOINT}/${index}`, exercise);

        return data as ExerciseMutationResponse;
    } catch (error: any) {
        return createApiError(error, 'Unable to update the exercise.');
    }
};

export const deleteExerciseByTag = async (tag: number): Promise<void> => {
    try {
        await axios.delete(`${EXERCISES_ENDPOINT}/tag/${tag}`);
    } catch (error: any) {
        return createApiError(error, 'Unable to delete the exercise.');
    }
};

export const uploadExerciseVideo = async (
    file: File,
    overrideExistingFile = false,
): Promise<ExerciseVideoUploadItem> => {
    try {
        const payload = new FormData();

        payload.append('file', file);
        payload.append('overrideExistingFile', overrideExistingFile ? 'true' : 'false');

        const { data } = await axios.post(EXERCISE_VIDEO_UPLOAD_ENDPOINT, payload, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return (data as ExerciseVideoUploadResponse).item;
    } catch (error: any) {
        if (error.response?.status === 409) {
            const conflictError = new ApiError(error.response?.data?.message || 'A video with this file name already exists.');

            (conflictError as ApiError & { conflict: boolean }).conflict = true;
            throw conflictError;
        }

        if (error.response?.status === 400) {
            throw new ApiError(error.response?.data?.message || 'Unable to upload the exercise video.');
        }

        throw new Error(error.response?.data?.message || error.message || 'Unable to upload the exercise video.');
    }
};